import React, { useState } from 'react';
import type { NodeViewProps } from '@tiptap/react';
import { Box, Button, IconButton, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import VideocamOutlined from '@mui/icons-material/VideocamOutlined';
import Audiotrack from '@mui/icons-material/Audiotrack';
import AttachFile from '@mui/icons-material/AttachFile';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import Link from '@mui/icons-material/Link';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import Add from '@mui/icons-material/Add';
import { useTranslate } from 'react-admin';
import { BlockFrame } from './BlockFrame';
import { AttrTextField } from './AttrFields';
import { MediaPickerDialog, type MediaAccept, type PickedMedia } from '../../../../common/MediaPickerDialog';
import { useMediaObjectUrl } from '../../../../../hooks/useMediaObjectUrl';
import { formatFileSize, getEmbedUrl } from '../blockUtils';

export { formatFileSize, getEmbedUrl };

const mediaSrc = (m: PickedMedia) => `/api/media/${m.uuid}`;

const PickButton: React.FC<{ label: string; accept: MediaAccept; onPick: (m: PickedMedia) => void; selectedUuid?: string; variant?: 'contained' | 'outlined' | 'text' }> = ({ label, accept, onPick, selectedUuid, variant = 'outlined' }) => {
    const [open, setOpen] = useState(false);
    return (
        <>
            <Button size="small" variant={variant} onClick={() => setOpen(true)}>{label}</Button>
            <MediaPickerDialog open={open} onClose={() => setOpen(false)} accept={accept} onSelect={onPick} selectedUuid={selectedUuid} />
        </>
    );
};

const EmptyZone: React.FC<{ icon: React.ReactNode; text: string; children: React.ReactNode }> = ({ icon, text, children }) => (
    <Box className="oa-block-dropzone" sx={{ minHeight: 120 }}>
        {icon}
        <Typography variant="body2" color="text.secondary">{text}</Typography>
        <Stack direction="row" spacing={1}>{children}</Stack>
    </Box>
);

/* ───────────────────────── Video ───────────────────────── */

interface Chapter { time: number; label: string }

export const VideoBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const { src, poster, caption, mediaFileUuid, chapters: rawChapters } = node.attrs as { src: string; poster: string; caption: string; mediaFileUuid: string; chapters?: Chapter[] };
    const chapters: Chapter[] = Array.isArray(rawChapters) ? rawChapters : [];
    const videoUrl = useMediaObjectUrl(src);
    const posterUrl = useMediaObjectUrl(poster);

    const setChapters = (next: Chapter[]) => updateAttributes({ chapters: next });

    return (
        <BlockFrame type="video-block" icon={VideocamOutlined} title={translate('blockEditor.types.video.label')} selected={selected} meta={caption || undefined}>
            {src ? (
                <Box>
                    {videoUrl ? (
                        <video src={videoUrl} poster={posterUrl ?? undefined} controls className="oa-block-media" />
                    ) : (
                        <Box className="oa-block-dropzone" sx={{ minHeight: 120 }}><Typography variant="body2" color="text.secondary">{translate('blockEditor.views.loadingMedia')}</Typography></Box>
                    )}
                </Box>
            ) : (
                <EmptyZone icon={<VideocamOutlined sx={{ fontSize: 32, color: 'var(--oa-text-2)' }} />} text={translate('blockEditor.views.video.empty')}>
                    <PickButton label={translate('blockEditor.views.pickFromLibrary')} accept="video" variant="contained" onPick={(m) => updateAttributes({ src: mediaSrc(m), mediaFileUuid: m.uuid })} />
                </EmptyZone>
            )}
            {selected && (
                <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <AttrTextField size="small" fullWidth label={translate('blockEditor.image.caption')} value={caption} onValueChange={(v) => updateAttributes({ caption: v })} />
                        <PickButton label={src ? translate('blockEditor.views.replace') : translate('blockEditor.views.pickFromLibrary')} accept="video" selectedUuid={mediaFileUuid} onPick={(m) => updateAttributes({ src: mediaSrc(m), mediaFileUuid: m.uuid })} />
                        <PickButton label={poster ? translate('blockEditor.views.video.posterChange') : translate('blockEditor.views.video.poster')} accept="image" onPick={(m) => updateAttributes({ poster: mediaSrc(m) })} />
                        {poster && <Button size="small" color="inherit" onClick={() => updateAttributes({ poster: '' })}>{translate('blockEditor.views.video.posterRemove')}</Button>}
                    </Stack>
                    <Box>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary">{translate('blockEditor.views.video.chapters', { smart_count: chapters.length })}</Typography>
                            <Button size="small" startIcon={<Add />} onClick={() => setChapters([...chapters, { time: 0, label: '' }])}>{translate('blockEditor.views.video.addChapter')}</Button>
                        </Stack>
                        {chapters.map((ch, i) => (
                            <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                <AttrTextField size="small" type="number" label={translate('blockEditor.views.video.seconds')} value={String(ch.time)} onValueChange={(v) => setChapters(chapters.map((c, k) => (k === i ? { ...c, time: parseInt(v, 10) || 0 } : c)))} sx={{ width: 110 }} inputProps={{ min: 0 }} />
                                <AttrTextField size="small" fullWidth label={translate('blockEditor.views.video.chapterTitle')} value={ch.label} onValueChange={(v) => setChapters(chapters.map((c, k) => (k === i ? { ...c, label: v } : c)))} />
                                <IconButton size="small" onClick={() => setChapters(chapters.filter((_, k) => k !== i))} aria-label={translate('ra.action.remove')}><DeleteOutline fontSize="small" /></IconButton>
                            </Stack>
                        ))}
                    </Box>
                </Stack>
            )}
        </BlockFrame>
    );
};

/* ───────────────────────── Audio ───────────────────────── */

export const AudioBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const { src, title, mediaFileUuid } = node.attrs as { src: string; title: string; mediaFileUuid: string };
    const url = useMediaObjectUrl(src);
    return (
        <BlockFrame type="audio-block" icon={Audiotrack} title={translate('blockEditor.types.audio.label')} selected={selected} meta={title || undefined}>
            {src ? (
                url ? <audio src={url} controls className="oa-block-media" /> : <Typography variant="body2" color="text.secondary">{translate('blockEditor.views.loadingMedia')}</Typography>
            ) : (
                <EmptyZone icon={<Audiotrack sx={{ fontSize: 32, color: 'var(--oa-text-2)' }} />} text={translate('blockEditor.views.audio.empty')}>
                    <PickButton label={translate('blockEditor.views.pickFromLibrary')} accept="audio" variant="contained" onPick={(m) => updateAttributes({ src: mediaSrc(m), mediaFileUuid: m.uuid, title: title || m.originalFilename })} />
                </EmptyZone>
            )}
            {selected && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
                    <AttrTextField size="small" fullWidth label={translate('blockEditor.views.audio.title')} value={title} onValueChange={(v) => updateAttributes({ title: v })} />
                    <PickButton label={src ? translate('blockEditor.views.replace') : translate('blockEditor.views.pickFromLibrary')} accept="audio" selectedUuid={mediaFileUuid} onPick={(m) => updateAttributes({ src: mediaSrc(m), mediaFileUuid: m.uuid, title: title || m.originalFilename })} />
                </Stack>
            )}
        </BlockFrame>
    );
};

/* ───────────────────────── File ───────────────────────── */

export const FileBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const { src, filename, filesize, mediaFileUuid } = node.attrs as { src: string; filename: string; filesize: number; mediaFileUuid: string };
    const onPick = (m: PickedMedia) => updateAttributes({ src: mediaSrc(m), mediaFileUuid: m.uuid, filename: m.originalFilename, filesize: m.size ?? 0, mimeType: m.mimeType ?? '' });
    return (
        <BlockFrame type="file-block" icon={AttachFile} title={translate('blockEditor.types.file.label')} selected={selected}>
            {src ? (
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <span className="oa-block-frame-icon"><AttachFile fontSize="small" /></span>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{filename || translate('blockEditor.views.file.unnamed')}</Typography>
                        {filesize > 0 && <Typography variant="caption" color="text.secondary">{formatFileSize(filesize)}</Typography>}
                    </Box>
                    {selected && <PickButton label={translate('blockEditor.views.replace')} accept="any" selectedUuid={mediaFileUuid} onPick={onPick} />}
                </Stack>
            ) : (
                <EmptyZone icon={<AttachFile sx={{ fontSize: 32, color: 'var(--oa-text-2)' }} />} text={translate('blockEditor.views.file.empty')}>
                    <PickButton label={translate('blockEditor.views.pickFromLibrary')} accept="any" variant="contained" onPick={onPick} />
                </EmptyZone>
            )}
        </BlockFrame>
    );
};

/* ───────────────────────── PDF ───────────────────────── */

export const PdfViewerBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const { src, filename, mediaFileUuid } = node.attrs as { src: string; filename: string; mediaFileUuid: string };
    const url = useMediaObjectUrl(src);
    const onPick = (m: PickedMedia) => updateAttributes({ src: mediaSrc(m), mediaFileUuid: m.uuid, filename: m.originalFilename });
    return (
        <BlockFrame type="pdf-viewer-block" icon={PictureAsPdfOutlined} title={translate('blockEditor.types.pdf_viewer.label')} selected={selected} meta={filename || undefined} actions={selected && src ? <PickButton label={translate('blockEditor.views.replace')} accept="document" selectedUuid={mediaFileUuid} onPick={onPick} /> : undefined} flush={!!src}>
            {src ? (
                url ? <iframe src={`${url}#toolbar=1&navpanes=0`} title={filename || 'PDF'} className="oa-block-pdf" /> : <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>{translate('blockEditor.views.loadingMedia')}</Typography>
            ) : (
                <EmptyZone icon={<PictureAsPdfOutlined sx={{ fontSize: 32, color: 'var(--oa-text-2)' }} />} text={translate('blockEditor.views.pdf.empty')}>
                    <PickButton label={translate('blockEditor.views.pickFromLibrary')} accept="document" variant="contained" onPick={onPick} />
                </EmptyZone>
            )}
        </BlockFrame>
    );
};

/* ───────────────────────── Embed ───────────────────────── */

export const EmbedBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const { url, caption, aspectRatio } = node.attrs as { url: string; caption: string; aspectRatio: string };
    const embedUrl = url ? getEmbedUrl(url) : null;
    const padding = aspectRatio === '4:3' ? '75%' : aspectRatio === '1:1' ? '100%' : '56.25%';

    return (
        <BlockFrame type="embed-block" icon={Link} title={translate('blockEditor.types.embed.label')} selected={selected} meta={caption || undefined}>
            {embedUrl ? (
                <Box sx={{ position: 'relative', width: '100%', paddingBottom: padding, borderRadius: 'var(--oa-radius-md, 8px)', overflow: 'hidden' }}>
                    <iframe src={embedUrl} title={caption || url} className="oa-block-embed" allowFullScreen sandbox="allow-scripts allow-same-origin allow-popups allow-presentation" />
                </Box>
            ) : url ? (
                <Typography variant="body2" color="text.secondary">{translate('blockEditor.views.embed.linkOnly', { url })}</Typography>
            ) : (
                <Typography variant="body2" color="text.secondary">{translate('blockEditor.views.embed.empty')}</Typography>
            )}
            {(selected || !url) && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
                    <AttrTextField size="small" fullWidth label={translate('blockEditor.views.embed.url')} placeholder="https://www.youtube.com/watch?v=…" value={url} onValueChange={(v) => updateAttributes({ url: v.trim() })} />
                    <AttrTextField size="small" fullWidth label={translate('blockEditor.image.caption')} value={caption} onValueChange={(v) => updateAttributes({ caption: v })} />
                    <AttrTextField size="small" select label={translate('blockEditor.views.embed.ratio')} value={aspectRatio || '16:9'} onValueChange={(v) => updateAttributes({ aspectRatio: v })} sx={{ minWidth: 110 }}>
                        {['16:9', '4:3', '1:1'].map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                    </AttrTextField>
                </Stack>
            )}
        </BlockFrame>
    );
};

export const RemoveIconButton: React.FC<{ onClick: () => void; disabled?: boolean; label: string }> = ({ onClick, disabled, label }) => (
    <Tooltip title={label}>
        <span>
            <IconButton size="small" onClick={onClick} disabled={disabled} aria-label={label}><DeleteOutline fontSize="small" /></IconButton>
        </span>
    </Tooltip>
);
